import { useCallback, useMemo, useState } from 'react';
import {
  keepPreviousData,
  useQueries,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { Result } from 'neverthrow';
import { resourceViewOf } from '@cotera/sdk/core';
import { toast } from '@cotera/client/app/components/ui';
import { CocoEvent, useAnalytics } from '@cotera/client/app/etc';
import type {
  ApiErr,
  ResourceAccess,
  TenantedClient,
} from '@cotera/client/app/etc/api/typed-client';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import {
  shareTargetKey,
  shareTargetLabel,
  type ShareTarget,
} from './share-targets';

/**
 * The server's own reason when it gave one. The share endpoints send their
 * refusals ("Workflows can't be shared with people outside your organization")
 * without the `{ t, err }` envelope, which the client files under `server`, so
 * those count too; only a crash's generic text falls back.
 */
export const apiErrorMessage = (error: ApiErr, fallback: string): string => {
  const message =
    error.t === 'application' ||
    (error.t === 'server' && error.err.errorType !== 'InternalServerError')
      ? error.err.message
      : undefined;
  return message !== undefined && message.trim() !== '' ? message : fallback;
};

type Level = 'view' | 'edit' | 'admin';

/**
 * Who can reach a set of items, merged. With one item this is just its access;
 * with several (a chat and its artifacts, a multi-selection) every person and
 * the org appear once, with how many of the items they can open — so the list
 * never shows only the first item while the buttons act on all of them.
 */
export type CombinedAccess = {
  total: number;
  owner: ResourceAccess['owner'];
  people: Array<ResourceAccess['people'][number] & { onCount: number }>;
  org: { permission: Level; onCount: number } | null;
  inherited: ResourceAccess['inherited'];
  externalOrgs: ResourceAccess['externalOrgs'];
  /**
   * What sharing these also shares because they use it (an agent's documents,
   * a workflow's agents and datasets), leaving out anything already chosen.
   */
  linked: ResourceAccess['linked'];
  /** Emailed invites not opened yet; one link can cover several items. */
  pendingInvites: ResourceAccess['pendingInvites'];
  viewer: ResourceAccess['viewer'];
  /** The items the viewer may change sharing on; changes go only to these. */
  manageable: ShareTarget[];
  /**
   * Items that sit in a folder, with that folder and whether its sharing
   * reaches them. Only items that load are listed.
   */
  inFolders: Array<{
    target: ShareTarget;
    folder: NonNullable<ResourceAccess['folder']>;
  }>;
};

export function useCombinedAccess(targets: readonly ShareTarget[]): {
  access: CombinedAccess | null;
  isPending: boolean;
  error: Error | null;
} {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const results = useQueries({
    queries: targets.map((target) => ({
      queryKey: queryKeys.resource.access({
        orgId,
        type: target.type,
        id: target.id,
      }),
      queryFn: async () => {
        const result = await client.resource.getAccess({
          type: target.type,
          id: target.id,
        });
        if (result.isErr()) {
          throw new Error(
            apiErrorMessage(result.error, 'Could not load access')
          );
        }
        return result.value;
      },
      staleTime: 10_000,
      placeholderData: keepPreviousData,
    })),
    combine: combineAccessResults,
  });

  return useMemo(() => {
    const [subject] = results.data;
    if (subject === undefined || subject === null) {
      return {
        access: null,
        isPending: results.pending,
        error: results.error,
      };
    }
    const loaded = results.data.flatMap((access, index) => {
      const target = targets[index];
      return access === null || target === undefined
        ? []
        : [{ access, target }];
    });

    const people = new Map<string, CombinedAccess['people'][number]>();
    let orgCount = 0;
    let orgLevel: Level | null = null;
    const inherited = new Map<string, ResourceAccess['inherited'][number]>();
    const externalOrgs = new Map<
      string,
      ResourceAccess['externalOrgs'][number]
    >();
    const chosen = new Set(targets.map((t) => shareTargetKey(t)));
    const linked = new Map<string, ResourceAccess['linked'][number]>();
    const pendingInvites = new Map<
      string,
      ResourceAccess['pendingInvites'][number]
    >();
    // Levels come from the first item a person appears on — the chat, when it
    // is ticked — since that is the item the panel is about.
    for (const { access } of loaded) {
      for (const person of access.people) {
        const seen = people.get(person.userId);
        if (seen === undefined) {
          people.set(person.userId, { ...person, onCount: 1 });
        } else {
          seen.onCount += 1;
        }
      }
      if (access.orgAccess !== null) {
        orgCount += 1;
        orgLevel ??= access.orgAccess.permission;
      }
      for (const folder of access.inherited) {
        inherited.set(folder.folderId, folder);
      }
      for (const org of access.externalOrgs) {
        externalOrgs.set(org.orgId, org);
      }
      for (const invite of access.pendingInvites) {
        pendingInvites.set(invite.linkId, invite);
      }
      for (const item of access.linked) {
        const key = shareTargetKey(item);
        if (!chosen.has(key)) {
          linked.set(key, item);
        }
      }
    }

    return {
      access: {
        total: targets.length,
        owner: subject.owner,
        people: [...people.values()],
        org:
          orgLevel === null
            ? null
            : { permission: orgLevel, onCount: orgCount },
        inherited: [...inherited.values()],
        externalOrgs: [...externalOrgs.values()],
        linked: [...linked.values()],
        pendingInvites: [...pendingInvites.values()],
        viewer: subject.viewer,
        manageable: loaded
          .filter(({ access }) => access.viewer.canManage)
          .map(({ target }) => target),
        inFolders: loaded.flatMap(({ access, target }) =>
          access.folder === null ? [] : [{ target, folder: access.folder }]
        ),
      },
      isPending: false,
      error: null,
    };
  }, [results, targets]);
}

/** Module-level so `useQueries` can keep its result stable between renders. */
const combineAccessResults = (
  results: ReadonlyArray<{
    data?: ResourceAccess;
    isPending: boolean;
    error: Error | null;
  }>
) => ({
  data: results.map((r) => r.data ?? null),
  pending: results.some((r) => r.isPending && r.data === undefined),
  error: results.find((r) => r.error !== null)?.error ?? null,
});

export function useActiveShareLinks(
  target: ShareTarget | null,
  enabled: boolean
) {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  return useQuery({
    queryKey:
      target === null
        ? [orgId, 'resource', 'shareLinks', 'none']
        : queryKeys.resource.shareLinks({
            orgId,
            type: target.type,
            id: target.id,
          }),
    queryFn: async () => {
      if (target === null) {
        return [];
      }
      const result = await client.resource.listActiveShareLinks({
        type: target.type,
        id: target.id,
      });
      if (result.isErr()) {
        throw new Error(apiErrorMessage(result.error, 'Could not load links'));
      }
      return result.value.links;
    },
    enabled: target !== null && enabled,
    staleTime: 10_000,
  });
}

/** A resource's display title, resolved from the cache or the server. */
export function useShareTargetTitle(target: ShareTarget): string {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  const named = target.name?.trim();
  const { data } = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: target.type,
      id: target.id,
    }),
    queryFn: () => client.resource.get({ type: target.type, id: target.id }),
    staleTime: 60_000,
    enabled: named === undefined || named === '',
  });
  return useMemo(() => {
    if (named !== undefined && named !== '') {
      return named;
    }
    if (data !== undefined && data.isOk()) {
      return resourceViewOf(data.value).title();
    }
    return `Untitled ${shareTargetLabel(target.type).toLowerCase()}`;
  }, [data, named, target.type]);
}

const titlesFromResults = (
  results: ReadonlyArray<{
    data?: Awaited<ReturnType<TenantedClient['resource']['get']>>;
  }>
): Array<string | null> =>
  results.map((result) =>
    result.data !== undefined && result.data.isOk()
      ? resourceViewOf(result.data.value).title()
      : null
  );

/**
 * Titles for many resources at once, keyed by {@link shareTargetKey} — for
 * lists that sort, group or filter by name, which per-row lookups can't do.
 * Shares cache entries with {@link useShareTargetTitle}.
 */
export function useShareTargetTitles(
  targets: readonly ShareTarget[]
): Map<string, string> {
  const client = useTenantedClient();
  const orgId = useWhoami((s) => s.org.id);
  // A module-level `combine` keeps the result referentially stable while no
  // query changes, so the map below is rebuilt only when a title arrives.
  const titles = useQueries({
    queries: targets.map((target) => {
      const named = target.name?.trim();
      return {
        queryKey: queryKeys.resource.get({
          orgId,
          type: target.type,
          id: target.id,
        }),
        queryFn: () =>
          client.resource.get({ type: target.type, id: target.id }),
        staleTime: 60_000,
        enabled: named === undefined || named === '',
      };
    }),
    combine: titlesFromResults,
  });
  return useMemo(() => {
    const byKey = new Map<string, string>();
    targets.forEach((target, index) => {
      const named = target.name?.trim();
      byKey.set(
        shareTargetKey(target),
        named !== undefined && named !== ''
          ? named
          : titles[index] ??
              `Untitled ${shareTargetLabel(target.type).toLowerCase()}`
      );
    });
    return byKey;
  }, [targets, titles]);
}

type ShareChange =
  | 'person_added'
  | 'outside_person_invited'
  | 'person_changed'
  | 'person_removed'
  | 'invite_removed'
  | 'org_shared'
  | 'org_changed'
  | 'org_unshared'
  | 'external_org_removed'
  | 'folder_sharing_limited'
  | 'folder_sharing_followed';

/**
 * Runs one sharing change — against every target, or once for all of them —
 * then refreshes whatever reads
 * access: the dialog itself, headers and avatar stacks (`resource.get`) and
 * lists. Stops at the first failure and says why — a partial share is still
 * visible in the dialog, which reloads either way.
 */
export function useApplyShareChange(targets: readonly ShareTarget[]) {
  const queryClient = useQueryClient();
  const orgId = useWhoami((s) => s.org.id);
  const analytics = useAnalytics();
  const [pending, setPending] = useState(false);

  const refresh = useCallback(async () => {
    await Promise.all(
      targets.flatMap((target) => [
        queryClient.invalidateQueries({
          queryKey: queryKeys.resource.access({
            orgId,
            type: target.type,
            id: target.id,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.resource.get({
            orgId,
            type: target.type,
            id: target.id,
          }),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.resource.shareLinks({
            orgId,
            type: target.type,
            id: target.id,
          }),
        }),
      ])
    );
    void queryClient.invalidateQueries({
      queryKey: [orgId, 'resource', 'listPage'],
    });
    void queryClient.invalidateQueries({
      queryKey: [orgId, 'resource', 'list'],
    });
  }, [orgId, queryClient, targets]);

  /**
   * Resolves to each call's result, in order, or null if one failed (which
   * has already been reported).
   */
  const runAll = useCallback(
    async <T>(
      change: ShareChange,
      calls: ReadonlyArray<() => Promise<Result<T, ApiErr>>>,
      successMessage?: string,
      changed: readonly ShareTarget[] = targets
    ): Promise<T[] | null> => {
      setPending(true);
      try {
        const values: T[] = [];
        for (const call of calls) {
          const result = await call();
          if (result.isErr()) {
            toast.error(
              apiErrorMessage(result.error, "Couldn't update sharing")
            );
            return null;
          }
          values.push(result.value);
        }
        // Nothing ran: don't claim success or record a change that didn't
        // happen. Guards against a caller handing an empty target set (e.g. a
        // viewer who can't manage anything) and reading the toast as "done".
        if (calls.length > 0) {
          analytics.track(CocoEvent.ShareAccessChanged, {
            change,
            targetCount: changed.length,
            targetTypes: changed.map((t) => t.type),
          });
          if (successMessage !== undefined) {
            toast.success(successMessage);
          }
        }
        return values;
      } finally {
        await refresh();
        setPending(false);
      }
    },
    [analytics, refresh, targets]
  );

  /** One call per target. */
  const apply = useCallback(
    <T>(
      change: ShareChange,
      run: (target: ShareTarget, index: number) => Promise<Result<T, ApiErr>>,
      successMessage?: string
    ): Promise<T[] | null> =>
      runAll(
        change,
        targets.map((target, index) => () => run(target, index)),
        successMessage
      ),
    [runAll, targets]
  );

  /** One call per target among `some`, such as the items in a folder. */
  const applyTo = useCallback(
    <T>(
      some: readonly ShareTarget[],
      change: ShareChange,
      run: (target: ShareTarget) => Promise<Result<T, ApiErr>>
    ): Promise<T[] | null> =>
      runAll(
        change,
        some.map((target) => () => run(target)),
        undefined,
        some
      ),
    [runAll]
  );

  /** One call that covers every target, such as an emailed invite. */
  const applyOnce = useCallback(
    async <T>(
      change: ShareChange,
      run: () => Promise<Result<T, ApiErr>>
    ): Promise<T | null> => {
      const values = await runAll(change, [run]);
      return values?.[0] ?? null;
    },
    [runAll]
  );

  return { apply, applyTo, applyOnce, pending, refresh };
}
